import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { emittedDocApi } from "./axios";
import {
  DataI,
  ServiceResponseI,
  PropsEmittedDocumentI,
  EmittedDocumentI,
} from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

const getEmittedDocuments = async ({
  pagination,
  sorting,
  globalFilter,
  dateFrom,
  dateTo,
  dateField,
}: PropsEmittedDocumentI): Promise<
  ServiceResponseI<DataI<EmittedDocumentI>>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    params.append("dateField", dateField ?? "");
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);

    const { data } = await emittedDocApi.get<
      ServiceResponseI<DataI<EmittedDocumentI>>
    >("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

/**
 * Trae TODOS los documentos emitidos de un rango de fechas (sin paginar),
 * para exportar a Excel — mismo patrón que
 * received-document/hook.ts getAllReceivedDocumentsByRange: primero pide el
 * total (limit:1) y luego lo trae todo en una sola página.
 */
export const getAllEmittedDocumentsByRange = async (
  dateFrom: string,
  dateTo: string,
  dateField?: string,
): Promise<EmittedDocumentI[]> => {
  const token = ValidateCurrentToken();
  if (!token) throw new Error("TOKEN INVALIDO");

  const baseParams = {
    dateField: dateField || "fechaEmision",
    dateFrom,
    dateTo,
  };

  const { data: firstPage } = await emittedDocApi.get<
    ServiceResponseI<DataI<EmittedDocumentI>>
  >("", {
    params: { page: 1, limit: 1, ...baseParams },
    headers: { Authorization: token },
  });

  const total = firstPage?.data?.total ?? 0;
  if (total === 0) return [];

  const { data: allPage } = await emittedDocApi.get<
    ServiceResponseI<DataI<EmittedDocumentI>>
  >("", {
    params: { page: 1, limit: total, ...baseParams },
    headers: { Authorization: token },
  });

  return allPage?.data?.dato ?? [];
};

export const deleteEmittedDocument = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await emittedDocApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      {
        headers: { Authorization: validateToken },
      },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const updateEmittedDocumentEstado = async (
  id: string,
  estado: "AUTORIZADO" | "ANULADO",
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await emittedDocApi.patch<ServiceResponseI<string>>(
      `/${id}/estado`,
      { estado },
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getEmittedDocumentPdf = async (id: string): Promise<Blob> => {
  const token = ValidateCurrentToken();
  if (!token) throw new Error("TOKEN INVALIDO");
  try {
    const { data } = await emittedDocApi.get(`/${id}/pdf`, {
      headers: { Authorization: token },
      responseType: "blob",
    });
    return data;
  } catch (err: any) {
    // Con responseType blob el cuerpo del error llega como Blob: se lee para
    // mostrar el mensaje real del backend en vez de "status code 400".
    const body = err?.response?.data;
    if (body instanceof Blob) {
      try {
        const parsed = JSON.parse(await body.text());
        if (parsed?.message) throw new Error(parsed.message);
      } catch (inner: any) {
        if (inner instanceof SyntaxError) throw err;
        throw inner;
      }
    }
    throw err;
  }
};

export const updateEmittedDocumentComprador = async (
  id: string,
  payload: { rucReceptor?: string; razonSocialReceptor?: string },
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await emittedDocApi.patch<ServiceResponseI<string>>(
      `/${id}`,
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

/**
 * Sincroniza "Comprobantes electrónicos emitidos" del SRI para un rango de
 * fechas — el backend intenta primero con sesión reutilizada (sin login) y
 * cae a login completo si hace falta. El timeout es generoso porque el SRI
 * se recorre día por día (ver EmittedDocumentService.sincronizarRango).
 */
export const syncEmitidosRango = async (payload: {
  fechaDesde: string;
  fechaHasta: string;
}) => {
  const t = ValidateCurrentToken();
  if (!t) throw new Error("TOKEN INVALIDO");
  const { data } = await emittedDocApi.post("/sync", payload, {
    headers: { Authorization: t },
    timeout: 900_000, //  15 min: recorrer el rango de días tarda
  });
  return data;
};

/**
 * Sincroniza "Comprobantes anulados" del SRI para un rango de meses —
 * marca como ANULADO cualquier documento existente cuya clave de acceso
 * aparezca en el rango. Mismo mecanismo de sesión reutilizada/login que
 * syncEmitidosRango (ver EmittedDocumentService.sincronizarAnuladosRango).
 */
export const syncAnuladosRango = async (payload: {
  mesDesde: string;
  mesHasta: string;
}) => {
  const t = ValidateCurrentToken();
  if (!t) throw new Error("TOKEN INVALIDO");
  const { data } = await emittedDocApi.post("/sync-anulados", payload, {
    headers: { Authorization: t },
    timeout: 900_000,
  });
  return data;
};

export interface ReconcileEmitidosResultI {
  revisados: number;
  completados: number;
  sinCambio: number;
}

/**
 * "Completar desde SRI": el backend consulta el XML autorizado de cada
 * emitida que quedó sin comprador/XML y llena comprador, correo, teléfono,
 * dirección y totales. Consulta al SRI por cada una, de ahí el timeout.
 */
export const reconcileEmittedDocuments = async (): Promise<
  ServiceResponseI<ReconcileEmitidosResultI>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await emittedDocApi.post<
      ServiceResponseI<ReconcileEmitidosResultI>
    >("/reconcile", {}, {
      headers: { Authorization: validateToken },
      timeout: 600_000,
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error)?.message ?? "Error");
  }
};

export const useEmittedDocuments = ({
  sorting,
  pagination,
  globalFilter,
  dateFrom,
  dateTo,
  dateField,
}: PropsEmittedDocumentI) => {
  const queryClient = useQueryClient();

  const emittedDocumentQuery = useQuery(
    [
      "emitted-documents",
      { pagination, sorting, globalFilter, dateFrom, dateTo, dateField },
    ],
    () =>
      getEmittedDocuments({
        pagination,
        sorting,
        globalFilter,
        dateFrom,
        dateTo,
        dateField,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  const deleteOneMutation = useMutation(
    (id: string) => deleteEmittedDocument(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["emitted-documents"]);
      },
    },
  );

  const deleteBulkMutation = useMutation(
    (ids: string[]) => Promise.all(ids.map((id) => deleteEmittedDocument(id))),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["emitted-documents"]);
      },
    },
  );

  const updateCompradorMutation = useMutation(
    ({
      id,
      payload,
    }: {
      id: string;
      payload: { rucReceptor?: string; razonSocialReceptor?: string };
    }) => updateEmittedDocumentComprador(id, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["emitted-documents"]);
      },
    },
  );

  const updateEstadoMutation = useMutation(
    ({ id, estado }: { id: string; estado: "AUTORIZADO" | "ANULADO" }) =>
      updateEmittedDocumentEstado(id, estado),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["emitted-documents"]);
      },
    },
  );

  // Sync manual de "Comprobantes emitidos" del SRI para un rango de fechas
  // elegido desde el modal. Al terminar, invalida la query de la tabla
  // para que se refresque sola con los documentos nuevos.
  const syncEmitidosMutation = useMutation(
    (payload: { fechaDesde: string; fechaHasta: string }) =>
      syncEmitidosRango(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["emitted-documents"]);
      },
    },
  );

  // Sync manual de "Comprobantes anulados" del SRI para un rango de meses.
  // Al terminar, invalida la query de la tabla para que se vean los
  // documentos recién marcados como ANULADO.
  const syncAnuladosMutation = useMutation(
    (payload: { mesDesde: string; mesHasta: string }) =>
      syncAnuladosRango(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["emitted-documents"]);
      },
    },
  );

  const reconcileMutation = useMutation(() => reconcileEmittedDocuments(), {
    onSuccess: () => {
      queryClient.invalidateQueries(["emitted-documents"]);
    },
  });

  return {
    emittedDocumentQuery,
    reconcileMutation,
    deleteOneMutation,
    deleteBulkMutation,
    updateCompradorMutation,
    updateEstadoMutation,
    syncEmitidosMutation,
    syncAnuladosMutation,
  };
};